import { useState } from 'react';
import { resolveImagePath } from '@/utils/imageMap';
import galleryData from '@/data/gallery.json';

export function Gallery() {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const openLightbox = (index: number) => {
    setCurrentImageIndex(index);
    setLightboxOpen(true);
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
  };

  const nextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % galleryData.length);
  };

  const previousImage = () => {
    setCurrentImageIndex((prev) => (prev - 1 + galleryData.length) % galleryData.length);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowRight') nextImage();
    if (e.key === 'ArrowLeft') previousImage();
    if (e.key === 'Escape') closeLightbox();
  };

  return (
    <section className="py-16 bg-white">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold text-gray-800 mb-4">Our Gallery</h2>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          {galleryData.slice(0, 8).map((image, index) => (
            <div
              key={image.id}
              className="aspect-square overflow-hidden rounded-lg group cursor-pointer hover:shadow-lg transition-all duration-300"
              onClick={() => openLightbox(index)}
            >
              <div className="block h-full">
                <img
                  src={resolveImagePath(image.src)}
                  alt={image.alt}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                />
              </div>
            </div>
          ))}
        </div>

        <div className="text-center mt-8">
          <div className="bg-gray-50 rounded-lg p-8">
            <h4 className="text-2xl font-semibold text-gray-800 mb-8">Our Fleet</h4>

            <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto mb-8">
              <div className="bg-white rounded-lg p-6 shadow-sm">
                <h5 className="text-lg font-semibold text-teal-600 mb-3">28-Foot Open Hull Boat</h5>
                <p className="text-gray-700 leading-relaxed">
                  Operates daily from George Town's Exuma Yacht Club, providing comfortable
                  and reliable water taxi service around Elizabeth Harbour.
                </p>
              </div>

              <div className="bg-white rounded-lg p-6 shadow-sm">
                <h5 className="text-lg font-semibold text-teal-600 mb-3">26-Foot Catamaran</h5>
                <p className="text-gray-700 leading-relaxed">
                  Picks up from Augusta Bay, offering stable and spacious transportation
                  for passengers traveling throughout the harbour area.
                </p>
              </div>
            </div>

            <div className="text-sm text-gray-600 border-t border-gray-200 pt-6">
              <p className="font-medium">Fully Licensed & Insured</p>
              <p>All captains hold Class B captain licenses</p>
            </div>
          </div>
        </div>

        {/* Lightbox Modal */}
        {lightboxOpen && (
          <div
            className="fixed inset-0 bg-black bg-opacity-90 z-50 flex items-center justify-center"
            onClick={closeLightbox}
            onKeyDown={handleKeyDown}
            role="dialog"
            aria-modal="true"
            tabIndex={-1}
          >
            {/* Close Button */}
            <button
              type="button"
              onClick={closeLightbox}
              className="absolute top-4 right-4 text-white text-4xl font-light hover:text-gray-300 transition-colors z-10"
              aria-label="Close lightbox"
            >
              ×
            </button>

            {/* Previous Button */}
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                previousImage();
              }}
              className="absolute left-4 text-white text-6xl font-light hover:text-gray-300 transition-colors"
              aria-label="Previous image"
            >
              ‹
            </button>

            {/* Image Container */}
            <div
              className="max-w-7xl max-h-[90vh] mx-4"
              onClick={(e) => e.stopPropagation()}
            >
              <img
                src={resolveImagePath(galleryData[currentImageIndex].src)}
                alt={galleryData[currentImageIndex].alt}
                className="max-w-full max-h-[90vh] object-contain rounded-lg"
              />
              <p className="text-white text-center mt-4 text-sm">
                {currentImageIndex + 1} / {galleryData.length}
              </p>
            </div>

            {/* Next Button */}
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                nextImage();
              }}
              className="absolute right-4 text-white text-6xl font-light hover:text-gray-300 transition-colors"
              aria-label="Next image"
            >
              ›
            </button>
          </div>
        )}
      </div>
    </section>
  );
}